import {useDispatch, useSelector} from 'react-redux'
import Overview from '../../assets/icons/Overview'
import {Badge, Box, Button, Paper, Stack} from '@mui/material'
import commonActions from '../../actions/common.actions'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {subscriptionTags} from '../../assets/constants'
import {useLocation} from 'react-router-dom'
import Holdings from '../../assets/icons/Holdings'
import websocketActions from '../../actions/websocket.actions'
import Trade from '../../assets/icons/Trade'
import NewsIcon from '../../assets/icons/NewsIcon'
import SecurityOverview from '../securityInfo/SecurityOverview'
import ScreenerSecurityOverview from '../securityInfo/ScreenerSecurityOverview'
import {WidgetListItems} from '../../util/sidebar'
import Positions from '../../assets/icons/Positions'
import {getNoOfOpenOrders, getNoOfPositions} from '../../selectors/orders'
import Watchlist from '../../assets/icons/watchlist'
import AlarmIcon from '../../assets/icons/AlarmIcon'
import {ExtraSmallButton, NonInteractiveTooltip} from '../custom'
import {mergeSideBarBottomButtonSx, sideBarBottomIconStackSx} from './sideBarBottomIconButtonSx'
import {HelpCenter} from '../help/HelpCenter'
import {setSteps} from '../../reducer/tour.reducer'
import ROUTES from '../../assets/routes'
import {matchRoute} from '../../util/routes'
import pathStepsMap from '../tour/tourStepsMap'
import {Calendars} from '../calendars/Calendars'
import {selectRightSidebarWidget} from '../../selectors/common'
import { GlobalNotificationDrawer } from '../GlobalNotification/GlobalNotificationDrawer'
import { isWidgetbarLayoutWidget } from '../../util/sidebar'
import ToursTipsIcon from "../../assets/icons/ToursTipsIcon";
import QuickWidgetsMenu from '../quickWidgetsMenu/quickWidgetsMenu'
import { WIDGETBAR_VIEW_ELEMENTS } from './widgetbarViews'
import WidgetbarPagesPanel from './WidgetbarPagesPanel'

const buttons = [
    {
        element: <Overview/>,
        label: 'Overview',
        code: WidgetListItems.OVERVIEW
    },
    {
        element: <Watchlist/>,
        label: 'Watchlist',
        code: WidgetListItems.WATCHLIST
    },
    {
        element: <AlarmIcon/>,
        label: 'Alert',
        code: WidgetListItems.ALERT
    },
    {
        element: <NewsIcon/>,
        label: 'News',
        code: WidgetListItems.MARKET_NEWS
    },
    {
        element: <Trade style={{fontSize: "40px"}}/>,
        label: 'Trade',
        code: WidgetListItems.TRADE
    },
    {
        element: <Positions/>,
        label: 'Positions/Orders',
        code: WidgetListItems.POSITIONS
    },
    // {
    //     element: <OrderIcon color="inherit" style={{fontSize: '40px'}}/>,
    //     label: 'Orders',
    //     code: WidgetListItems.ORDERS
    // },
    {
        element: <Holdings/>,
        label: 'Holdings',
        code: WidgetListItems.HOLDINGS
    }

]

const SideBarRight = ({blur}) => {
    const dispatch = useDispatch()
    const view = useSelector(selectRightSidebarWidget)
    const [rightSideWidgetView, setRightSideWidgetView] = useState<string | null>(view)
    const location = useLocation()
    
    const noOfOpenPositions = useSelector(getNoOfPositions)
    const noOfOpenOrders = useSelector(getNoOfOpenOrders)
    const isScreenerRoute = useMemo(() => !!matchRoute(location.pathname, [{ path: ROUTES.screener, key: 'screener' }]), [location.pathname])
    const overviewPanel = useMemo(
        () => (isScreenerRoute ? <ScreenerSecurityOverview /> : <SecurityOverview />),
        [isScreenerRoute]
    )

    const tourSteps = useMemo(() => {
        const routes = [
            {path: ROUTES.chart, key: 'chart'},
            {path: ROUTES.optionChain, key: 'option-chain'},
            {path: ROUTES.openInterest, key: 'multi-strike-chart'},
            {path: ROUTES.screener, key: 'screener'},
            {path: ROUTES.fiiDii, key: 'fii-dii'},
            {path: ROUTES.strategyBuilder, key: 'strategy-builder'}
        ]
        const matchedRoute = matchRoute(location.pathname, routes)
        if (matchedRoute && pathStepsMap[matchedRoute]) {
            return pathStepsMap[matchedRoute]
        }
        return []
    }, [location, pathStepsMap])

    const handleRightSideWidgetIconClick = useCallback((btnCode: WidgetListItems | null) => {
        const prevValue = rightSideWidgetView
        if (rightSideWidgetView === btnCode) {
            setRightSideWidgetView(null)
            commonActions.dispatchRightSideWidget(dispatch, null)
        } else {
            setRightSideWidgetView(btnCode)
            commonActions.dispatchRightSideWidget(dispatch, btnCode)
        }
        if (prevValue === WidgetListItems.OVERVIEW) {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: subscriptionTags.INDEX_COMPONENT,
                        securities: []
                    }
                ]
            })
        }
        if (prevValue === WidgetListItems.WATCHLIST) {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: subscriptionTags.WATCHLIST,
                        securities: []
                    }
                ]
            })
        }
    }, [dispatch, rightSideWidgetView])

    useEffect(() => {
        setRightSideWidgetView(view)
    }, [view])

    const activeWidgetContent = useMemo(() => {
        if (!isWidgetbarLayoutWidget(rightSideWidgetView)) return null
        if (rightSideWidgetView === WidgetListItems.OVERVIEW) {
            return overviewPanel
        }
        return WIDGETBAR_VIEW_ELEMENTS[rightSideWidgetView] ?? null
    }, [rightSideWidgetView, overviewPanel])

    const showWidgetbarPanel = isWidgetbarLayoutWidget(rightSideWidgetView)

    const onProTipClicked = useCallback(() => {
        dispatch(setSteps(tourSteps))
    }, [tourSteps])

    return (
        <>
            {
                showWidgetbarPanel ?
                    <WidgetbarPagesPanel blur={blur}>{activeWidgetContent}</WidgetbarPagesPanel>
                    : null
            }
            <Paper
                sx={{
                    gridArea: 'right-sidebar',
                    zIndex: 999,
                    borderRadius: '4px 4px 0 4px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    overflowY: 'auto',
                    marginLeft: 'var(--extra-small-spacing)',
                    minHeight: 0,
                    WebkitOverflowScrolling: 'touch'
                }}
                style={{ ...(blur ? { filter: 'blur(4px)', pointerEvents: 'none' } : {}) }}
            >
                <Box
                    id="side-bar-right-options"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minHeight: 0,
                        flex: '1 1 auto',
                        overflowY: 'auto'
                    }}
                >
                    {
                        buttons.map(
                            (btn, i) =>
                                <NonInteractiveTooltip arrow key={i} placement={'left'} title={btn.label}>
                                    <Button sx={{
                                        borderRadius: 0,
                                        maxHeight: 'screen',
                                        minWidth: 'max-content',
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        '&:hover': {
                                            backgroundColor: (theme) => (rightSideWidgetView === btn.code) ? theme.palette.background.default : null
                                        },
                                        backgroundColor: (theme) => (rightSideWidgetView === btn.code) ?
                                            theme.palette.background.default : null
                                    }}
                                        color={'inherit'}
                                        onClick={() => handleRightSideWidgetIconClick(btn.code as any)}
                                        key={i}
                                        data-event={`${btn.label} Icon Clicked`}
                                        data-event-action={rightSideWidgetView === btn.code ? 'Closed' : 'Opened'}
                                    >
                                        <Badge
                                            color="primary"
                                            sx={{
                                                display: 'inline-flex',
                                                flexDirection: 'column',
                                                alignItems: 'center'
                                            }}
                                            badgeContent={
                                                WidgetListItems.POSITIONS === btn.code ? noOfOpenPositions :
                                                    WidgetListItems.ORDERS === btn.code ? noOfOpenOrders : 0
                                            }
                                            overlap="circular"
                                        >
                                            {btn.element}
                                            {/*<Typography style={{ paddingBottom: '8px' }} variant="body2">*/}
                                            {/*    {btn.code}*/}
                                            {/*</Typography>*/}
                                        </Badge>
                                    </Button>
                                </NonInteractiveTooltip>
                        )
                    }

                </Box>
                <Stack alignItems={'center'} sx={{ width: '100%', flexShrink: 0, ...sideBarBottomIconStackSx }}>
                    <QuickWidgetsMenu />
                    <GlobalNotificationDrawer />
                    {tourSteps.length > 0 ?
                        <NonInteractiveTooltip arrow placement="left" title={'Tour & tips'}>
                            <span>
                                <ExtraSmallButton
                                    size="small"
                                    color="inherit"
                                    data-event={'Pro Tip Clicked'}
                                    id={'show-tour-button'}
                                    onClick={onProTipClicked}
                                    sx={mergeSideBarBottomButtonSx()}
                                >
                                    <ToursTipsIcon/>
                                </ExtraSmallButton>
                            </span>
                        </NonInteractiveTooltip>
                        : null
                    }
                    <Calendars/>
                    <HelpCenter/>
                </Stack>
            </Paper>
        </>
    )
}

export default memo(SideBarRight)